import { MESSAGE_NOTIFICATION_RECEIVED } from '../data/messages/notificationReceived';
import { setLang } from '../data/messages/setLang';
import { urlBase64ToUint8Array } from '../helpers/urlBase64ToUint8Array';
import { registerServiceWorker } from '../registerServiceWorker';

export class PushNotificationsClient {
    constructor(serviceWorkerPath, vapidPublicKey) {
        this.serviceWorkerPath = serviceWorkerPath;
        this.vapidPublicKey = vapidPublicKey;
    }

    /**
     * Checks whether the user has already been asked for permission to display
     * notifications.
     *
     * @returns {boolean} true if the user has already been asked for permission,
     * false otherwise.
     */
    isPermissionAsked() {
        try {
            const permission = Notification.permission;

            if (permission === 'granted') {
                // console.log('Permission already granted.');
                return true; // Permission has already been granted
            } else if (permission === 'denied') {
                // console.log('Permission was previously denied.');
                return true; // Permission was asked and denied
            }
        } catch (error) {
            console.error(error);
        }

        return false; // Permission hasn't been asked yet
    }

    /**
     * Checks whether the user has granted permission to display notifications.
     *
     * @returns {boolean} true if permission is granted, false otherwise.
     */
    hasPermission() {
        try {
            return Notification.permission === 'granted';
        } catch (error) {
            return false;
        }
    }

    /**
     * Requests user permission to show notifications.
     *
     * @returns {Promise<void>} The promise indicating permission was requested.
     */
    async requestPermission() {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                console.log('Permission granted');
                return true;
            }
        } catch (error) { }

        console.log('Permission denied');
        return false;
    }

    /**
     * Gets the push subscription.
     *
     * @returns {Promise<PushSubscription | null>} The subscription promise.
     */
    async getSubscription() {
        const registration = await registerServiceWorker(this.serviceWorkerPath);

        return registration.pushManager.getSubscription();
    }

    /**
     * Subscribes to the push service.
     *
     * @returns {Promise<PushSubscription>}
     *     The subscription promise.
     */
    async subscribe() {
        const registration = await registerServiceWorker(this.serviceWorkerPath);

        if (!registration) {
            console.error(`Service worker doesn't seem to be registered.`);
            return;
        }

        const existentSubscription = await registration.pushManager.getSubscription();
        if (existentSubscription) {
            return existentSubscription;
        }

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(this.vapidPublicKey),
        });

        return subscription;
    }

    async unsubscribe() {
        const subscription = await this.getSubscription();

        if (subscription) {
            await subscription.unsubscribe();
        }
    }

    async setLang(lang) {
        const registration = await registerServiceWorker(this.serviceWorkerPath);

        registration.active.postMessage(setLang(lang));
    }

    async onNotificationReceived(callback) {
        await registerServiceWorker(this.serviceWorkerPath);
        navigator.serviceWorker.ready.then(() => {
            navigator.serviceWorker.addEventListener('message', (event) => {
                if (event.data?.type === MESSAGE_NOTIFICATION_RECEIVED) {
                    callback(event.data.notification);
                }
            });
        });
    }
}
